import { add, format, isBefore, isSameDay } from 'date-fns';
import { currencyUSDFormatter } from '../common/currencyFormatters';
import { PjmRegistrationLeadTime, PjmRegistrationProgram } from './dto/enums';
import { EDC_DEP_MAP } from './values';
import { fromZonedTime } from 'date-fns-tz';
import { PJM_TZ } from '.';
import { getTimezoneFormattedTime } from '../date/date';

type ValidationResult = { valid: true } | { valid: false; error: Error };

// ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz01234567890 _-\/()[].#+*&:',
export const locationName = {
    REGEX: new RegExp(/^[\w\-_\/\\ \(\)#+*\[\]&:.,']+$/),
    errorMessage: 'Only alphanumeric characters, space and _-\/()[].#+*&:, symbols are allowed. Length 100 characters or less.',
    isValid: function (val: string): ValidationResult {
        if (String(val).length > 100) {
            return {
                valid: false,
                error: new Error(this.errorMessage),
            };
        }

        const isValid = this.REGEX.test(val);
        return isValid ? { valid: true } : { valid: false, error: new Error(this.errorMessage) };
    },
};

export const locationAddress = {
    REGEX: new RegExp(/^[\w\-_\/\\ \(\)#+*\[\]&:.,']+$/),
    errorMessage: 'Only alphanumeric characters, space and _-\/()[].#+*&:, symbols are allowed. Length 80 characters or less.',
    isValid: function (val: string): ValidationResult {
        if (String(val).length > 80) {
            return {
                valid: false,
                error: new Error(this.errorMessage),
            };
        }

        const isValid = this.REGEX.test(val);
        return isValid ? { valid: true } : { valid: false, error: new Error(this.errorMessage) };
    },
};

export const locationZipCode = {
    REGEX: new RegExp(/^[0-9-]+$/),
    errorMessage: 'Only numbers and "-" symbol are allowed. Length 10 characters or less.',
    isValid: function (val: string): ValidationResult {
        if (String(val).length > 10) {
            return {
                valid: false,
                error: new Error(this.errorMessage),
            };
        }

        const isValid = this.REGEX.test(val);
        return isValid ? { valid: true } : { valid: false, error: new Error(this.errorMessage) };
    },
};

export const locationEdcAccountNumber = {
    isValid: function (val: string, edcName: string): ValidationResult {
        const obj = EDC_DEP_MAP[edcName];
        if (!obj?.edcAccountNo) return { valid: true };
        const { min, max, numericOnly } = obj.edcAccountNo;

        const inRange = String(val).length >= min && String(val).length <= max;
        const correctCharacters = numericOnly ? new RegExp(/^[0-9]+$/).test(val) : new RegExp(/^[A-Za-z0-9]+$/).test(val);

        if (inRange && correctCharacters) {
            return { valid: true };
        }

        let error = new Error(`Must be between ${min} and ${max} length of digits${!numericOnly ? ' and letters' : ''}`);

        if (min === max) {
            error = new Error(`Must be exactly ${min} digits${!numericOnly ? ' and letters' : ''}`);
        }

        return { valid: false, error };
    },
};

export const locationZoneNumber = {
    isValid: function (val: string, edcName: string): ValidationResult {
        const obj = EDC_DEP_MAP[edcName];
        if (!obj.zone) return { valid: true };

        if (obj.zone.includes(val)) {
            return { valid: true };
        }

        return { valid: false, error: new Error(`Must be one of ${prettyPrintOptions(obj.zone)}`) };
    },
};

const strikePriceMaxByLeadTime = {
    [PjmRegistrationLeadTime.Quick_30]: 1849,
    [PjmRegistrationLeadTime.Short_60]: 1425,
    [PjmRegistrationLeadTime.Long_120]: 1100,
};

export const strikePrice = {
    isValid: function (price: number, leadTime?: PjmRegistrationLeadTime): ValidationResult {
        if (!price) return { valid: true };
        if (!leadTime) {
            leadTime = PjmRegistrationLeadTime.Quick_30;
        }

        const maxPrice = strikePriceMaxByLeadTime[leadTime];
        if (!maxPrice) return { valid: true };

        if (price <= maxPrice) {
            return { valid: true };
        }

        return {
            valid: false,
            error: new Error(`Strike Price can not be higher than ${currencyUSDFormatter.format(maxPrice)}`),
        };
    },
};

function prettyPrintOptions(options: string[]) {
    return `${options.map(it => `"${it}"`).join(', ')}`;
}

export function getDefaultRegistrationDates(program: PjmRegistrationProgram) {
    switch (program) {
        case PjmRegistrationProgram['Economic']: {
            // Dates editable: Effective -> Today + 2 days (12/06/2025); Termination -> 01/10/2027 (+400 days from start)
            const day = add(new Date(), { days: 2 });
            const startYear = day.getUTCFullYear();
            const startMonth = day.getUTCMonth();
            const startDay = day.getUTCDate();
            const startDate = fromZonedTime(new Date(startYear, startMonth, startDay, 0, 0, 0, 0), PJM_TZ);

            const endDate = add(startDate, { days: 400 });

            return { startDate, endDate };
        }
        case PjmRegistrationProgram['Load Management DR Full']: {
            // Dates hardcoded: Effective -> 06/01/2026; Termination -> 05/31/2027
            const startYear = new Date().getUTCFullYear();
            const startMonth = 5; // starts from zero - so it is 6th - June
            const startDay = 1;
            let startDate = fromZonedTime(new Date(startYear, startMonth, startDay, 0, 0, 0, 0), PJM_TZ);

            const today = new Date();
            if (today.valueOf() > startDate.valueOf()) {
                startDate = add(startDate, { years: 1 });
            }

            const endYear = startDate.getUTCFullYear() + 1;
            const endMonth = 4; // starts from zero
            const endDay = 31;
            const endDate = fromZonedTime(new Date(endYear, endMonth, endDay, 23, 59, 59, 0), PJM_TZ);

            return { startDate, endDate };
        }
        case PjmRegistrationProgram['Load Management DR Capacity Only']: {
            // Dates hardcoded: Effective -> 06/01/2026; Termination -> 05/31/2027
            const startYear = new Date().getUTCFullYear();
            const startMonth = 5; // starts from zero - so it is 6th - June
            const startDay = 1;
            let startDate = fromZonedTime(new Date(startYear, startMonth, startDay, 0, 0, 0, 0), PJM_TZ);

            const today = new Date();
            if (today.valueOf() > startDate.valueOf()) {
                startDate = add(startDate, { years: 1 });
            }

            const endYear = startDate.getUTCFullYear() + 1;
            const endMonth = 4; // starts from zero
            const endDay = 31;
            const endDate = fromZonedTime(new Date(endYear, endMonth, endDay, 23, 59, 59, 0), PJM_TZ);

            return { startDate, endDate };
        }
        case PjmRegistrationProgram['Emergency Energy Only']: {
            // Dates editable: Effective -> Today + 2 days (12/06/2025); Termination -> 12/06/2026
            const day = add(new Date(), { days: 2 });
            const startYear = day.getUTCFullYear();
            const startMonth = day.getUTCMonth();
            const startDay = day.getUTCDate();
            const startDate = fromZonedTime(new Date(startYear, startMonth, startDay, 23, 59, 59, 0), PJM_TZ);

            const endDate = add(startDate, { years: 1 });

            return { startDate, endDate };
        }
        case PjmRegistrationProgram['Price Responsive Demand']: {
            // Dates combined: Effective editable -> Today+1 day (12/05/2025); Termination -> 05/31/2026
            const day = add(new Date(), { days: 1 });
            const startYear = day.getUTCFullYear();
            const startMonth = day.getUTCMonth();
            const startDay = day.getUTCDate();
            const startDate = fromZonedTime(new Date(startYear, startMonth, startDay, 0, 0, 0, 0), PJM_TZ);

            const endYear = startYear + 1;
            const endMonth = 4; // starts from zero
            const endDay = 31;
            const endDate = fromZonedTime(new Date(endYear, endMonth, endDay, 23, 59, 59, 0), PJM_TZ);

            return { startDate, endDate };
        }
        case PjmRegistrationProgram['Peak Shaving Adjustment']: {
            // Dates hardcoded: Effective -> 06/01/2025; Termination -> 05/31/2026
            const startYear = new Date().getUTCFullYear();
            const startMonth = 5; // starts from zero - so it is 6th - June
            const startDay = 1;
            const startDate = fromZonedTime(new Date(startYear, startMonth, startDay, 0, 0, 0, 0), PJM_TZ);

            const endYear = startYear + 1;
            const endMonth = 4; // starts from zero
            const endDay = 31;
            const endDate = fromZonedTime(new Date(endYear, endMonth, endDay, 23, 59, 59, 0), PJM_TZ);

            return { startDate, endDate };
        }
        default: {
            const startYear = new Date().getUTCFullYear();
            const startMonth = 5; // starts from zero - so it is 6th - June
            const startDay = 1;
            let startDate = fromZonedTime(new Date(startYear, startMonth, startDay, 0, 0, 0, 0), PJM_TZ);

            const today = new Date();
            if (today.valueOf() > startDate.valueOf()) {
                startDate = add(startDate, { years: 1 });
            }

            const endYear = startDate.getUTCFullYear() + 1;
            const endMonth = 4; // starts from zero
            const endDay = 31;
            const endDate = fromZonedTime(new Date(endYear, endMonth, endDay, 23, 59, 59, 0), PJM_TZ);

            return { startDate, endDate };
        }
    }
}

export function validateRegistrationDates(program: PjmRegistrationProgram, startDate: Date, endDate: Date) {
    const DATE_FORMAT = 'MM/dd/yyyy';
    const defaultDates = getDefaultRegistrationDates(program);

    console.info(`default startDate=`, defaultDates.startDate.toISOString());
    console.info(`default endDate=`, defaultDates.endDate.toISOString());

    console.info(`startDate=`, startDate.toISOString());
    console.info(`endDate=`, endDate.toISOString());

    switch (program) {
        case PjmRegistrationProgram['Economic']: {
            // dates must be equal or after default start data
            // end dates must be after start date
            if (isBefore(startDate, defaultDates.startDate) || isBefore(endDate, startDate)) {
                return {
                    valid: false,
                    error: new Error(
                        `Effective date must be equal or after: "${getTimezoneFormattedTime(defaultDates.startDate, PJM_TZ, DATE_FORMAT)}" and Termination date must be after Effective date`
                    ),
                };
            }

            return {
                valid: true,
            };
        }
        case PjmRegistrationProgram['Load Management DR Full']:
        case PjmRegistrationProgram['Load Management DR Capacity Only']: {
            // dates must be exactly as default dates
            if (isSameDay(startDate, defaultDates.startDate) && isSameDay(endDate, defaultDates.endDate)) {
                return {
                    valid: true,
                };
            }

            return {
                valid: false,
                error: new Error(
                    `Effective date must be: "${getTimezoneFormattedTime(defaultDates.startDate, PJM_TZ, DATE_FORMAT)}" and Termination date: "${getTimezoneFormattedTime(defaultDates.endDate, PJM_TZ, DATE_FORMAT)}"`
                ),
            };
        }
        case PjmRegistrationProgram['Emergency Energy Only']: {
            // dates must be equal or after default start data
            // end dates must be after start date
            if (isBefore(startDate, defaultDates.startDate) || isBefore(endDate, startDate)) {
                return {
                    valid: false,
                    error: new Error(
                        `Effective date must be equal or after: "${getTimezoneFormattedTime(defaultDates.startDate, PJM_TZ, DATE_FORMAT)}" and Termination date must be after Effective date`
                    ),
                };
            }

            return {
                valid: true,
            };
        }
        case PjmRegistrationProgram['Price Responsive Demand']: {
            if (isBefore(startDate, defaultDates.startDate) || isBefore(endDate, startDate)) {
                return {
                    valid: false,
                    error: new Error(
                        `Effective date must be equal or after: "${getTimezoneFormattedTime(defaultDates.startDate, PJM_TZ, DATE_FORMAT)}" and Termination date must be after Effective date`
                    ),
                };
            }

            if (isSameDay(endDate, defaultDates.endDate)) {
                return {
                    valid: false,
                    error: new Error(`Termination date must be equal to: "${getTimezoneFormattedTime(defaultDates.endDate, PJM_TZ, DATE_FORMAT)}"`),
                };
            }

            return {
                valid: true,
            };
        }
        case PjmRegistrationProgram['Peak Shaving Adjustment']: {
            // dates must be exactly as default dates
            if (isSameDay(startDate, defaultDates.startDate) && isSameDay(endDate, defaultDates.endDate)) {
                return {
                    valid: true,
                };
            }

            return {
                valid: false,
                error: new Error(
                    `Effective date must be: "${getTimezoneFormattedTime(defaultDates.startDate, PJM_TZ, DATE_FORMAT)}" and Termination date: "${getTimezoneFormattedTime(defaultDates.endDate, PJM_TZ, DATE_FORMAT)}"`
                ),
            };
        }
        default: {
            return {
                valid: false,
                error: new Error('Unexpected registration program, cannot validate dates!'),
            };
        }
    }
}
